@if (share.panel()) {
  <div class="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
    <!-- Backdrop button -->
    <button
      type="button"
      class="appearance-none border-0 absolute inset-0 bg-slate-950/50 backdrop-blur-[2px] transition-opacity cursor-pointer"
      aria-label="Close share panel"
      (click)="close()"
    ></button>

    <!-- Side Drawer Container -->
    <aside class="share-drawer-panel relative z-10 flex h-full w-full max-w-[480px] flex-col bg-white shadow-2xl transition-transform overflow-hidden">
      <!-- Drawer Header -->
      <div class="drawer-header">
        <div class="header-top-row">
          <div class="header-left-group">
            @if (canGoBack) {
              <button type="button" class="btn-drawer-back" (click)="goBack()" title="Go back to options">
                <svg class="back-chevron-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                <span>Back</span>
              </button>
            }
            <span class="header-tag">SHARE</span>
          </div>
          <button type="button" class="btn-drawer-close" (click)="close()">Close</button>
        </div>
        <h2 class="drawer-title">{{ panelTitle }}</h2>
        <p class="drawer-subtitle">
          @if (share.panel() === 'platform') {
            Celebrate your progress and inspire others across your professional network.
          } @else if (share.panel() === 'learner360') {
            Generate a secure, verified snapshot of your competencies for recruiters.
          } @else if (share.panel() === 'community') {
            Share your milestone directly with your cohort on SahkaarX.
          } @else {
            Choose how you'd like to share this achievement.
          }
        </p>
      </div>

      <!-- Messages / Feedback Alerts -->
      @if (share.error()) {
        <div class="share-alert alert-error">
          <svg class="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ share.error() }}</span>
        </div>
      }
      @if (share.statusMessage()) {
        <div class="share-alert alert-success">
          <svg class="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>{{ share.statusMessage() }}</span>
        </div>
      }

      <div class="drawer-body flex-1 overflow-y-auto min-h-0">
        <!-- Panel 1: Platform & Achievement Share (Mockup Image 2) -->
        @if (share.panel() === 'platform') {
          <!-- Preview Card -->
          <div class="achievement-preview-card">
            <div class="preview-card-top">
              <div class="preview-trophy-badge">
                <svg class="preview-trophy-svg" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2" />
                  <path d="M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
                  <path d="M4 22h16" />
                  <path d="M10 14.66V17c0 .55-.45 1-1 1H7v2h10v-2h-2c-.55 0-1-.45-1-1v-2.34" />
                  <path d="M6 3h12v7a6 6 0 0 1-12 0V3z" fill="#fef3c7" stroke="#d97706" />
                </svg>
              </div>
              <div class="preview-text-info">
                <h3 class="preview-title">{{ achievementTitle }}</h3>
                <p class="preview-desc">{{ achievementDescription }}</p>
              </div>
            </div>

            <!-- Dynamic Metric Chips (Only shown when available) -->
            @if (careerFitScoreMetric || weeksMetric || skillsGainedMetric) {
              <div class="preview-metrics-strip">
                @if (careerFitScoreMetric) {
                  <div class="mini-stat-card">
                    <span class="mini-stat-label">Career Fit Score</span>
                    <span class="mini-stat-val text-green">{{ careerFitScoreMetric }}</span>
                  </div>
                }
                @if (weeksMetric) {
                  <div class="mini-stat-card">
                    <span class="mini-stat-label">Weeks</span>
                    <span class="mini-stat-val">{{ weeksMetric }}</span>
                  </div>
                }
                @if (skillsGainedMetric) {
                  <div class="mini-stat-card">
                    <span class="mini-stat-label">Skills Gained</span>
                    <span class="mini-stat-val">{{ skillsGainedMetric }}</span>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Personal Note Field -->
          <div class="personal-note-section">
            <label class="personal-note-label" for="sharePersonalNote">
              Add a personal note (optional)
            </label>
            <textarea
              id="sharePersonalNote"
              class="personal-note-textarea"
              maxlength="280"
              [(ngModel)]="personalNote"
              placeholder="Share your learning journey, key takeaways or next goals..."
              rows="4"
            ></textarea>
            <div class="char-counter">
              <span>{{ personalNote.length }} / 280</span>
            </div>
          </div>

          <!-- Share On Section -->
          <div class="share-on-section">
            <h4 class="share-on-heading">Share on</h4>
            <div class="share-channels-grid">
              <!-- LinkedIn -->
              <button type="button" class="channel-btn" (click)="share.openLinkedIn(personalNote || achievementDescription)">
                <svg class="channel-icon icon-linkedin" viewBox="0 0 24 24" fill="#0077b5">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.64a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z"/>
                </svg>
                <span>LinkedIn</span>
              </button>

              <!-- Facebook -->
              <button type="button" class="channel-btn" (click)="share.openFacebook()">
                <svg class="channel-icon icon-facebook" viewBox="0 0 24 24" fill="#1877f2">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
                </svg>
                <span>Facebook</span>
              </button>

              <!-- Copy Link -->
              <button type="button" class="channel-btn" (click)="onCopyLink()">
                <svg class="channel-icon icon-link" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                <span>{{ copied ? 'Copied!' : 'Copy link' }}</span>
              </button>

              <!-- More (Native Device Share) -->
              <button type="button" class="channel-btn" (click)="onNativeShare()" title="Share via device options">
                <svg class="channel-icon icon-more" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <circle cx="12" cy="12" r="1.25"></circle>
                  <circle cx="19" cy="12" r="1.25"></circle>
                  <circle cx="5" cy="12" r="1.25"></circle>
                </svg>
                <span>More</span>
              </button>
            </div>
          </div>
        }

        <!-- Panel 2: Options Selection -->
        @if (share.panel() === 'options' && share.optionsContext(); as ctx) {
          <div class="options-view-container">
            <div class="options-guidance-banner">
              <svg class="guidance-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span>Select the sharing format that matches your audience:</span>
            </div>

            <div class="options-stack">
              @for (option of ctx.options; track option.id) {
                <button
                  type="button"
                  class="option-item-card"
                  [class.is-disabled]="option.disabled || (loadingOptionId !== null && loadingOptionId !== option.id)"
                  [class.is-loading]="option.loading || loadingOptionId === option.id"
                  [disabled]="option.disabled || option.loading || loadingOptionId !== null || share.busy()"
                  (click)="onOption(option.id)"
                >
                  <div class="option-icon-box" [ngClass]="'icon-' + getOptionIconType(option.id)">
                    @if (getOptionIconType(option.id) === 'snapshot') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                        <circle cx="12" cy="13" r="4"></circle>
                      </svg>
                    } @else if (getOptionIconType(option.id) === 'live') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                    } @else if (getOptionIconType(option.id) === 'community') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    } @else if (getOptionIconType(option.id) === 'learner360') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    } @else {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                      </svg>
                    }
                  </div>

                  <div class="option-text-group">
                    <div class="option-title-row">
                      <span class="option-title">{{ option.title }}</span>
                      @if (option.loading || loadingOptionId === option.id) {
                        <span class="option-loading-pill">
                          <span class="option-loading-dot"></span>
                          Opening...
                        </span>
                      }
                      @if (option.disabled) {
                        <span class="option-disabled-badge">Consent required</span>
                      }
                    </div>
                    @if (option.subtitle) {
                      <span class="option-subtitle">{{ option.subtitle }}</span>
                    }
                  </div>

                  <div class="option-arrow-box">
                    @if (option.loading || loadingOptionId === option.id) {
                      <span class="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-solid border-slate-200 border-t-emerald-700"></span>
                    } @else {
                      <svg class="option-chevron-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    }
                  </div>
                </button>
              }
            </div>
          </div>
        }

        <!-- Panel 3: Learner 360 View -->
        @if (share.panel() === 'learner360') {
          <div class="panel-360-container">
            <!-- Hero Feature Banner -->
            <div class="hero-360-card">
              <div class="hero-360-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
              </div>
              <div class="hero-360-info">
                <h3 class="hero-360-title">Verified Portfolio Snapshot</h3>
                <p class="hero-360-desc">Create a recruiter-ready, tamper-proof view of your competencies, verified challenge solutions, and skill trajectory.</p>
              </div>
            </div>

            <!-- Expiry Duration Selector -->
            <div class="form-section-block">
              <label class="section-block-label">Share Link Expiry</label>
              <div class="expiry-pills-grid">
                <button
                  type="button"
                  class="expiry-pill"
                  [class.selected]="expiresInDays === 7"
                  (click)="selectExpiry(7)"
                >
                  <span class="expiry-duration">7 Days</span>
                  <span class="expiry-note">Quick review</span>
                </button>
                <button
                  type="button"
                  class="expiry-pill"
                  [class.selected]="expiresInDays === 14"
                  (click)="selectExpiry(14)"
                >
                  <span class="expiry-duration">14 Days</span>
                  <span class="expiry-note">Standard</span>
                </button>
                <button
                  type="button"
                  class="expiry-pill"
                  [class.selected]="expiresInDays === 30"
                  (click)="selectExpiry(30)"
                >
                  <span class="expiry-duration">30 Days</span>
                  <span class="expiry-note">Extended</span>
                </button>
              </div>
            </div>

            <!-- Project Evidence Toggle Card -->
            <div class="evidence-toggle-card" (click)="includeEvidence = !includeEvidence">
              <div class="evidence-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
              </div>
              <div class="evidence-text-wrap">
                <span class="evidence-title">Include Project Evidence</span>
                <span class="evidence-desc">Allows viewers to inspect test suites, GitHub repositories, and execution logs.</span>
              </div>
              <div class="toggle-switch" [class.active]="includeEvidence">
                <div class="toggle-thumb"></div>
              </div>
            </div>

            @if (canShareWithAccount) {
              <div class="evidence-toggle-card" (click)="shareWithAccount = !shareWithAccount">
                <div class="evidence-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div class="evidence-text-wrap">
                  <span class="evidence-title">Share with my account portal</span>
                  <span class="evidence-desc">Make this 360 available to your referral or partner account.</span>
                </div>
                <div class="toggle-switch" [class.active]="shareWithAccount">
                  <div class="toggle-thumb"></div>
                </div>
              </div>
            }

            <!-- Access PIN Card -->
            <div class="form-section-block">
              <label class="section-block-label" for="accessPinInput">
                Passcode Security (Optional)
              </label>
              <div class="pin-input-wrapper">
                <svg class="pin-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  id="accessPinInput"
                  class="pin-field-input"
                  [type]="showPin ? 'text' : 'password'"
                  [(ngModel)]="accessPin"
                  placeholder="Set 4-6 digit numeric PIN"
                  maxlength="6"
                />
                <button type="button" class="btn-toggle-eye" (click)="togglePinVisibility()" title="Toggle PIN visibility">
                  @if (showPin) {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  } @else {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  }
                </button>
              </div>
              <span class="section-helper-text">Recipients must enter this PIN to unlock and view your portfolio.</span>
            </div>
          </div>
        }

        <!-- Panel 4: Community Post Compose -->
        @if (share.panel() === 'community' && share.communityContext(); as ctx) {
          <div class="panel-community-container">
            <!-- Cohort Header Badge -->
            <div class="cohort-highlight-card">
              <div class="cohort-icon-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div class="cohort-badge-info">
                <span class="cohort-badge-tag">SAHKAARX COMMUNITY</span>
                <p class="cohort-badge-desc">Post directly to your cohort feed to inspire and connect with peers.</p>
              </div>
            </div>

            <!-- Milestone Card Preview -->
            @if (ctx.headline || ctx.body) {
              <div class="community-preview-card">
                @if (ctx.headline) {
                  <h4 class="community-preview-headline">{{ ctx.headline }}</h4>
                }
                @if (ctx.body) {
                  <p class="community-preview-body">{{ ctx.body }}</p>
                }
              </div>
            }

            <!-- Quick Cohort Prompts -->
            <div class="quick-prompts-row">
              <span class="quick-prompt-label">Quick message:</span>
              <div class="quick-chips-wrap">
                @for (prompt of quickCohorts; track prompt) {
                  <button
                    type="button"
                    class="quick-chip-btn"
                    (click)="applyQuickCohort(prompt)"
                  >
                    {{ prompt }}
                  </button>
                }
              </div>
            </div>

            <!-- Personal Message Area -->
            <div class="form-section-block">
              <label class="section-block-label" for="communityNote">
                Your Note to the Cohort
              </label>
              <textarea
                id="communityNote"
                class="personal-note-textarea"
                maxlength="500"
                [ngModel]="share.communityShareText()"
                (ngModelChange)="share.communityShareText.set($event)"
                placeholder="Share reflections, lessons learned, or shout-outs..."
                rows="4"
              ></textarea>
              <div class="char-counter">
                <span>{{ share.communityShareText().length }} / 500</span>
              </div>
            </div>
            @if (share.communityContext()?.metrics?.length) {
              <div class="form-section-block">
                <span class="section-block-label">Verified progress (choose up to 3)</span>
                @for (metric of share.communityContext()!.metrics!; track metric.key) {
                  <label class="quick-chip-btn"><input type="checkbox" [checked]="share.communityMetricKeys().includes(metric.key)" (change)="share.toggleCommunityMetric(metric.key)"> {{ metric.label }}: {{ metric.value }}</label>
                }
              </div>
            }
            <div class="form-section-block">
              <label class="section-block-label">Key takeaway</label>
              <input class="personal-note-textarea" maxlength="280" [ngModel]="share.communityTakeaway()" (ngModelChange)="share.communityTakeaway.set($event)" placeholder="What did you learn?">
              <label class="section-block-label">Next goal</label>
              <input class="personal-note-textarea" maxlength="280" [ngModel]="share.communityNextGoal()" (ngModelChange)="share.communityNextGoal.set($event)" placeholder="What are you working toward next?">
            </div>

          </div>
        }
      </div>

      <!-- Drawer Footer (Fixed at bottom just like learning-task-panel) -->
      @if (share.panel() === 'platform') {
        <div class="border-t border-slate-100 bg-white px-6 py-4 shrink-0">
          <button
            type="button"
            class="btn-share-submit"
            [disabled]="share.busy()"
            (click)="onShareAchievement()"
          >
            @if (share.busy()) {
              <span class="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-solid border-white/40 border-t-white mr-1.5"></span>
              <span>Sharing...</span>
            } @else {
              <span>Share</span>
            }
          </button>
        </div>
      } @else if (share.panel() === 'learner360') {
        <div class="border-t border-slate-100 bg-white px-6 py-4 shrink-0">
          <button
            type="button"
            class="btn-share-submit"
            [disabled]="share.busy()"
            (click)="create360()"
          >
            @if (share.busy()) {
              <span class="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-solid border-white/40 border-t-white mr-1.5"></span>
              <span>Generating secure link...</span>
            } @else {
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              <span>Create 360° Portfolio Link</span>
            }
          </button>
        </div>
      } @else if (share.panel() === 'community') {
        <div class="border-t border-slate-100 bg-white px-6 py-4 shrink-0">
          <button
            type="button"
            class="btn-share-submit"
            [disabled]="share.busy()"
            (click)="share.submitCommunityPost()"
          >
            @if (share.busy()) {
              <span class="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-solid border-white/40 border-t-white mr-1.5"></span>
              <span>Publishing post...</span>
            } @else {
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              <span>Post on SahkaarX</span>
            }
          </button>
        </div>
      }
    </aside>
  </div>
}
